// CRM 2.0 · M12 Template Builder: one template version — scan card, checks, blanks with their settings
// (Page view = the Word file with chips, List view = the blanks table), Accept all, Replace file, Make a copy,
// Edit as new version (a Live version), Send for
// approval, and Approve / Return for the approver (prototype wizard steps 1, 2 and 5, plus vApprove).
// The steps are pages: 2 Map fields & edit (with the New page lines), 3 QR, 5 Send for approval / Approval; the
// step pills, Back and Next move between them.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import cls from "./Templates.module.css";
import { StepPills } from "./NewTemplate";
import BlankSettingModal from "./BlankSettingModal";
import MapFieldsPage, { fixTitle } from "./MapFieldsPage";
import PageSetup from "./PageSetup";
import EditInWord from "./EditInWord";
import ConfirmModal from "./ConfirmModal";
import CopyTemplateModal from "./CopyTemplateModal";
import QrStep from "./QrStep";
import BookingPreview from "./BookingPreview";
import { STATUS, EDITABLE, BLANK_KINDS, SUGGESTION_SOURCE, canDo, fmtDate, kindName, pickTag, isApplicantField, inApplicantBlock } from "./templateCommon";
import {
  getTemplateVersion, listDocFields, getTemplateBuilderSettings, setBlankSettings, acceptSuggestions,
  sendForApproval, approveTemplate, returnTemplate, replaceTemplateFile, getTemplateFileLink, getTemplatePreviewPdf, clearCarryNote, deleteTemplateDraft,
  copyTemplate, newTemplateVersion,
} from "../../Services/Templates";
import { errorTost, successTost, infoTost } from "../../Services/Toast";
import { presentLoader, dismissLoader } from "../../Services/loader";

const FILTERS = { ALL: "All", NONE: "No setting yet", SUGGESTED: "Has a suggestion", SET: "Set" };

function Yes({ ok, children }) {
  return <span className={ok ? cls.ok : cls.bad}>{children} {ok ? "✓" : "✕"}</span>;
}

function ScanCard({ scan }) {
  if (!scan) return null;
  const m = scan.margins || {};
  return (
    <div className={cls.kv}>
      <div>Pages</div><div>{scan.pages ?? "not counted (LibreOffice not found on the server)"}</div>
      <div>Page size</div><div><Yes ok={scan.pageSize?.allowed}>{scan.pageSize?.name} {scan.pageSize?.widthIn} × {scan.pageSize?.heightIn} in</Yes></div>
      <div>Margins</div><div>top {m.topIn} · bottom {m.bottomIn} · left {m.leftIn} · right {m.rightIn} in{m.mirrored ? " · mirrored for binding" : ""}</div>
      <div>Fonts</div><div>{(scan.fonts || []).map((f) => <span key={f.name} style={{ marginRight: "1.2rem" }}>{f.installed === null ? `${f.name} ?` : <Yes ok={f.installed}>{f.name}</Yes>}</span>)}</div>
      <div>Fields already in the file</div><div>{scan.mergeFields?.count ? `${scan.mergeFields.count} old merge fields (${scan.mergeFields.names.slice(0, 4).join(", ")}${scan.mergeFields.count > 4 ? " …" : ""})` : "None"}</div>
      <div>Blanks found</div><div>{scan.blanks?.count} · {Object.entries(scan.blanks?.byKind || {}).map(([k, n]) => `${n} ${BLANK_KINDS[k] || k}`).join(" · ")}</div>
      <div>Sections</div><div>{scan.sections?.count} ({scan.sections?.clauseRows} clause rows)</div>
      <div>Tables</div><div>{scan.tables?.count}{scan.tables?.biggest ? ` (the biggest has ${scan.tables.biggest.rows} rows and ${scan.tables.biggest.textShare}% of the text)` : ""}</div>
      <div>Comments / tracked changes</div><div><Yes ok={!scan.comments && !scan.trackedChanges}>{scan.comments} / {scan.trackedChanges}</Yes></div>
    </div>
  );
}

function settingText(b, fieldsByKey) {
  if (b.removed) return <span className={cls.bad}>Removed (page fix)</span>;
  if (b.notBlank) return <span className={cls.muted}>Not a blank (printed as typed)</span>;
  if (b.setting === "DATA" && pickTag(b.applicantPick)) return <span><b>{fieldsByKey[b.fieldKey]?.label || b.fieldKey}</b> · {pickTag(b.applicantPick)} applicant<br /><span className={cls.mono}>{b.fieldKey}</span></span>;
  if (b.setting === "DATA") return <span><b>{fieldsByKey[b.fieldKey]?.label || b.fieldKey}</b><br /><span className={cls.mono}>{b.fieldKey}</span></span>;
  if (b.setting === "LEAVE_BLANK") return `Leave blank · ${b.leaveAs === "SPACE" ? "space" : "line"}`;
  if (b.setting === "FIXED_TEXT") return `“${b.fixedText}”`;
  return <span className={cls.bad}>No setting yet</span>;
}

function TemplateVersion() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useSelector((state) => state.Auth.user);
  const replaceRef = useRef(null);
  const [detail, setDetail] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [copying, setCopying] = useState(false);
  const [fields, setFields] = useState([]);
  const [settings, setSettings] = useState({});
  const [filter, setFilter] = useState("ALL");
  const [view, setView] = useState("PAGE");
  const [editing, setEditing] = useState(null);
  const [note, setNote] = useState("");
  const [problems, setProblems] = useState([]);
  const [returning, setReturning] = useState(false);
  const [comments, setComments] = useState("");
  const [previewed, setPreviewed] = useState(null); // Approval: the booking the approver previewed it with
  const [tried, setTried] = useState(false); // Approval: a preview was tried (Return is possible even when it failed)
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(0); // blank settings / page fixes on their way to the server
  const [shownApplicants, setShownApplicants] = useState(2); // Buyer card switch; the Preview PDF uses it too
  const [step2, setStep2] = useState("MAP"); // DC-206: step 2 tab, Map fields | Edit in Word
  const [step, setStep] = useState(null); // the step page shown (null: the version's own step)
  const [focus, setFocus] = useState(null); // What's pending · Show me: where Map fields scrolls to
  const [more, setMore] = useState(false); // step 2 "More options": scan card, checks, page setup, list view
  const topRef = useRef(null);

  const load = useCallback(async () => {
    presentLoader();
    try {
      const [d, s] = await Promise.all([getTemplateVersion(id), getTemplateBuilderSettings()]);
      setDetail(d);
      setSettings(s?.values || {});
      setFields(await listDocFields(d.version.templateTypeKey));
    } catch (e) {
      errorTost(e.message);
    } finally {
      dismissLoader();
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setStep2("MAP"); setStep(null); setPreviewed(null); setTried(false); }, [id]);

  const fieldsByKey = useMemo(() => Object.fromEntries(fields.map((f) => [f.key, f])), [fields]);
  if (!detail) return <div className={cls.page} />;

  const { version, blanks } = detail;
  const scan = version.scan || {};
  const editable = EDITABLE.includes(version.status);
  const canEdit = editable && canDo(user, "Edit");
  // DC-206: while the version is open in Edit in Word, Map fields (and the rest of step 2) is locked for it
  const inWord = version.wordEdit;
  const canMap = canEdit && !inWord;
  const noSetting = blanks.filter((b) => !b.setting && !b.removed && !b.notBlank).length;
  const fixes = version.edits || [];
  const blocks = (version.checkResult || []).filter((c) => c.level === "BLOCK");
  const warns = (version.checkResult || []).filter((c) => c.level !== "BLOCK");
  const lastReturn = [...(version.approvals || [])].reverse().find((a) => a.decision === "RETURN");
  const shown = blanks.filter((b) => (filter === "NONE" ? !b.setting : filter === "SUGGESTED" ? b.suggestionSource && b.suggestionSource !== "NONE" : filter === "SET" ? !!b.setting : true));
  const current = step || (editable ? 2 : 5);
  const done = editable ? [1] : version.status === "IN_APPROVAL" ? [1, 2, 3, 4] : [1, 2, 3, 4, 5];
  const goStep = (n) => {
    setStep(n);
    if (topRef.current) topRef.current.scrollIntoView({ block: "start" });
    if (n === 5) load(); // What's pending is worked out by the server: fresh after the changes in steps 2 and 3
  };
  const pending = detail.pending || [];
  // Show me: step 2, Map fields, page view, at the place
  const showMe = (item) => {
    setStep2("MAP");
    setView("PAGE");
    setFocus({ ...item.show, n: Date.now() });
    setStep(2);
  };
  // step 2 jobs (simple view): one chip each over the draft; a click goes to the place, or to the step
  const blockItem = (k) => ((version.blocks && version.blocks.items) || []).find((i) => i.blockKey === k);
  const firstToFill = blanks.filter((b) => !b.setting && !b.removed && !b.notBlank).sort((a, b) => a.blankNo - b.blankNo)[0];
  const buyer = blockItem("BUYER");
  const signs = blockItem("SIGNATURE");
  const jobs = [
    { name: "Buyer details", done: !!buyer, todo: !buyer, hint: buyer ? "" : "one click",
      title: "The buyer's lines print once for each buyer: the system finds them", go: () => showMe({ show: { buyerJob: true } }) },
    { name: "Signatures", done: !!signs, hint: signs ? "" : "choose the line",
      title: "One numbered line per buyer for signing", go: () => showMe({ show: { signJob: true } }) },
    { name: "Fill the spaces", done: noSetting === 0, todo: noSetting > 0, hint: noSetting ? `${noSetting} to fill` : "",
      title: "Every space needs a field, hand-written or fixed words", go: () => firstToFill && showMe({ show: { blanks: [firstToFill.blankNo] } }) },
    { name: "Pages & QR", title: "Step 3", go: () => goStep(3) },
    { name: "Check with a real booking", title: "Step 4", go: () => goStep(4) },
    { name: "Send for approval", hint: pending.length ? `${pending.length} left` : "", title: "Step 5", go: () => goStep(5) },
  ];
  const nav = (back, next) => (
    <div className={cls.stepNav}>
      {back ? <button className={cls.btn} onClick={() => goStep(back[0])}>← {back[1]}</button> : <span />}
      {next ? <button className={`${cls.btn} ${cls.primary}`} onClick={() => goStep(next[0])}>Next: {next[1]} →</button> : <span />}
    </div>
  );

  async function run(fn, okMessage) {
    presentLoader();
    try {
      const result = await fn();
      if (okMessage) successTost(typeof okMessage === "function" ? okMessage(result) : okMessage);
      await load();
      return result;
    } catch (e) {
      errorTost(e.message);
      if (e.details) setProblems(e.details);
      return null;
    } finally {
      dismissLoader();
    }
  }

  // One blank: shown at once, saved in the background; only the saved row comes back (no reload).
  // On an error the blank goes back to what it was.
  // a read Replace file / Edit in Word note goes away
  const clearBtn = (list, r) => canMap && (
    <> <button className={cls.link} onClick={async () => {
      try {
        const res = await clearCarryNote(id, { list, blankNo: r.blankNo, added: !!r.added });
        setDetail((d) => ({ ...d, version: { ...d.version, scan: { ...d.version.scan, carryOver: res.carryOver } } }));
      } catch (e) { errorTost(e.message || "The note could not be cleared"); }
    }}>Clear</button></>
  );
  const putBlanks = (rows) => setDetail((d) => {
    const byNo = Object.fromEntries(rows.map((r) => [r.blankNo, r]));
    return { ...d, blanks: d.blanks.map((b) => (byNo[b.blankNo] ? { ...byNo[b.blankNo], removed: b.removed } : b)) };
  });

  async function saveBlank(change) {
    setEditing(null);
    const before = detail.blanks.find((b) => b.blankNo === change.blankNo);
    putBlanks([{ ...before, setting: change.setting, fieldKey: change.fieldKey || null, leaveAs: change.leaveAs || null, fixedText: change.fixedText || null, applicantPick: change.applicantPick || null }]);
    setSaving((n) => n + 1);
    try {
      const r = await setBlankSettings(id, [change]);
      if (r.errors?.length) {
        putBlanks([before]);
        errorTost(r.errors[0].error);
        return false;
      }
      putBlanks(r.blanks || []);
      return true;
    } catch (e) {
      putBlanks([before]);
      errorTost(e.message);
      return false;
    } finally {
      setSaving((n) => n - 1);
    }
  }

  // page fixes came back from the server: the version's list and which blanks are removed with them
  function putFixes(page) {
    const removed = new Set(page.removedBlanks || []);
    const notBlank = new Set(page.notBlanks || []);
    setDetail((d) => ({
      ...d,
      version: { ...d.version, edits: page.edits },
      blanks: d.blanks.map((b) => ({ ...b, removed: removed.has(b.blankNo), notBlank: notBlank.has(b.blankNo) })),
    }));
  }

  // DC-205: blanks Legal added (or took back) on the page
  function putAddedBlanks(added, deleted) {
    const gone = new Set(deleted);
    setDetail((d) => ({ ...d, blanks: [...d.blanks.filter((b) => !gone.has(b.blankNo)), ...added].sort((a, b) => a.blankNo - b.blankNo) }));
  }

  async function replaceFile(file) {
    if (!file) return;
    presentLoader();
    try {
      const saved = await replaceTemplateFile(id, file);
      const report = saved.scan?.carryOver?.blanks;
      successTost(`Version ${saved.versionNo} made${report ? `: ${report.carried} settings carried over, ${report.needsReassign.length} need re-assign` : ""}`);
      navigate(`/home/Templates/${saved.docTemplateId}`);
    } catch (e) {
      errorTost(e.message);
    } finally {
      dismissLoader();
    }
  }

  // Edit as new version: pressed again, the server returns the draft it already made from this version
  async function editAsNewVersion() {
    presentLoader();
    try {
      const saved = await newTemplateVersion(id);
      successTost(saved.existed ? `Draft v${saved.versionNo} was already made from this version: opened it` : `Draft v${saved.versionNo} made: every setting carried over`);
      navigate(`/home/Templates/${saved.docTemplateId}`);
    } catch (e) {
      errorTost(e.message);
    } finally {
      dismissLoader();
    }
  }

  // the tab is opened on the click (so the browser does not block it) and shows the PDF when it is ready
  async function previewPdf() {
    const tab = window.open("", "_blank");
    if (tab) tab.document.write('<p style="font:16px Inter,Segoe UI,sans-serif;padding:24px;color:#475467">Making preview… a long agreement takes about half a minute.</p>');
    setPreviewing(true);
    try {
      const { blob, warnings } = await getTemplatePreviewPdf(id, shownApplicants);
      if (warnings) infoTost(warnings);
      const url = URL.createObjectURL(blob);
      if (tab) tab.location.href = url;
      else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 10 * 60 * 1000);
    } catch (e) {
      if (tab) tab.close();
      errorTost(e.message);
    } finally {
      setPreviewing(false);
    }
  }

  async function openFile() {
    try {
      const { url } = await getTemplateFileLink(id);
      window.open(url, "_blank", "noopener");
    } catch (e) {
      errorTost(e.message);
    }
  }

  return (
    <div className={cls.page} ref={topRef}>
      <button className={cls.back} onClick={() => navigate("/home/Templates")}>← Templates</button>
      <div className={cls.head}>
        <div className={cls.headText}>
          <h1 className={cls.title}>{version.templateName}</h1>
          <p className={cls.hint}>
            {kindName(settings, version.templateTypeKey)} · version {version.versionNo} · {version.originalName}{" "}
            <span className={`${cls.chip} ${(STATUS[version.status] || {}).chip || ""}`} style={{ marginLeft: ".8rem" }}>{(STATUS[version.status] || {}).label}</span>
          </p>
        </div>
        <div className={cls.headActions}>
          <button className={cls.btn} onClick={previewPdf} disabled={previewing} title="Sample data, with your page fixes; not for signing">
            {previewing ? "Making preview…" : "Preview PDF"}
          </button>
          <button className={cls.btn} onClick={openFile}>Download Word file</button>
          {canDo(user, "Edit") && (
            <>
              <button className={cls.btn} disabled={!!inWord} title={inWord ? "Open in Edit in Word: save or close it there first" : undefined}
                onClick={() => replaceRef.current && replaceRef.current.click()}>Replace file</button>
              <input ref={replaceRef} type="file" accept=".docx" hidden onChange={(e) => replaceFile(e.target.files[0])} />
            </>
          )}
          {canDo(user, "Edit") && version.status === "LIVE" && (
            <button className={cls.btn} onClick={editAsNewVersion}
              title="The next draft of this template from this file, every setting carried over; this version stays Live until the new one is approved">
              Edit as new version
            </button>
          )}
          {canDo(user, "Create") && (
            <button className={cls.btn} onClick={() => setCopying(true)} title="A new template with its own name, the same file and every setting">Make a copy</button>
          )}
          {canDo(user, "Delete draft") && version.status === "DRAFT" && (
            <span title={version.deleteProblem || undefined}>
              <button className={`${cls.btn} ${cls.danger}`} disabled={!!version.deleteProblem} onClick={() => setConfirmDelete(true)}>Delete draft</button>
            </span>
          )}
        </div>
      </div>
      {copying && (
        <CopyTemplateModal version={version} onCancel={() => setCopying(false)}
          onConfirm={async (name) => {
            try {
              const saved = await copyTemplate(id, name);
              successTost(`${name} made (Draft v${saved.versionNo}): every setting carried over`);
              setCopying(false);
              navigate(`/home/Templates/${saved.docTemplateId}`);
            } catch (e) {
              errorTost(e.message);
            }
          }} />
      )}
      {confirmDelete && (
        <ConfirmModal title={`Delete draft v${version.versionNo} of ${version.templateName}?`} text="You can recover it later: Templates › Status: Deleted › Recover."
          onCancel={() => setConfirmDelete(false)}
          onConfirm={async () => {
            try {
              await deleteTemplateDraft(id);
              successTost(`Deleted draft v${version.versionNo} of ${version.templateName}`);
              navigate("/home/Templates");
            } catch (e) {
              errorTost(e.message);
              setConfirmDelete(false);
            }
          }} />
      )}
      <StepPills current={current} done={done} open={[2, 3, 4, 5]} onGo={goStep} />
      {editable && (
        <div className={cls.actionbar} style={{ marginTop: "-.8rem" }}>
          <span className={cls.savedNote}>Draft · every change is saved as you make it · last saved {fmtDate(version.last_updated_at)}</span>
          <button className={cls.btn} onClick={() => { successTost("Draft saved. Continue any time from Templates."); navigate("/home/Templates"); }}>Save draft and close</button>
        </div>
      )}

      {version.status === "RETURNED" && lastReturn && (
        <div className={cls.blockBox}><b>Returned by {lastReturn.byName || "the approver"} on {fmtDate(lastReturn.at)}:</b> {lastReturn.comments}</div>
      )}
      {version.status === "LIVE" && <div className={cls.okBox}>Live: new drafts use this version.</div>}

      {current === 2 && <>
      {/* simple view: the jobs of this step, the draft big; the rest under More options */}
      <div className={cls.jobBar}>
        {jobs.map((j) => (
          <button key={j.name} type="button" className={`${cls.job} ${j.done ? cls.jobDone : j.todo ? cls.jobTodo : ""}`} title={j.title} onClick={j.go}>
            {j.done ? "✓" : "●"} {j.name}{j.hint ? <span className={cls.jobHint}>· {j.hint}</span> : null}
          </button>
        ))}
        <span style={{ flex: 1 }} />
        {editable && canEdit && (
          <button className={cls.btn} onClick={() => setStep2(step2 === "WORD" ? "MAP" : "WORD")} title="Add or remove clauses, change margins or tables in the Word editor">
            {step2 === "WORD" ? "← Back to the draft" : "Big changes? Open in Word"}
          </button>
        )}
        <button className={cls.btn} aria-expanded={more} onClick={() => setMore((m) => !m)}>More options {more ? "▴" : "▾"}</button>
      </div>
      {!!blocks.length && !more && (
        <div className={cls.blockBox}>
          <b>{blocks.length} problem{blocks.length > 1 ? "s" : ""} in the Word file stop sending:</b> {blocks.map((c) => c.message).join(" · ")}{" "}
          <button className={cls.link} onClick={() => setMore(true)}>Details</button>
        </div>
      )}
      {scan.carryOver && ((scan.carryOver.blanks.needsReassign || []).length + (scan.carryOver.blanks.needsRecheck || []).length) > 0 && !more && (
        <div className={cls.sugBox} style={{ marginTop: 0, marginBottom: "1.2rem" }}>
          After the last file change, {(scan.carryOver.blanks.needsReassign || []).length + (scan.carryOver.blanks.needsRecheck || []).length} setting(s) could not be carried over and need a look.{" "}
          <button className={cls.link} onClick={() => setMore(true)}>Show</button>
        </div>
      )}

      {more && <>
      <div className={cls.panel}>
        <h2 className={cls.panelTitle}>Scan card</h2>
        <ScanCard scan={scan} />
      </div>

      <div className={cls.panel}>
        <h2 className={cls.panelTitle}>Checks</h2>
        {!blocks.length && !warns.length && <p className={cls.ok}>No problems found in the file ✓</p>}
        {!!blocks.length && (
          <div className={cls.blockBox}>
            <b>Fix in Word, then Replace file — these stop Send for approval:</b>
            <ul className={cls.issues}>{blocks.map((c) => <li key={c.code + c.message}>{c.message}</li>)}</ul>
          </div>
        )}
        {!!warns.length && <ul className={cls.issues}>{warns.map((c) => <li key={c.code + c.message}>{c.message}</li>)}</ul>}
        {scan.carryOver && (
          <div style={{ marginTop: "1.6rem" }}>
            <p><b>{scan.copiedFrom ? `Copied from ${scan.copiedFrom.templateName} v${scan.copiedFrom.versionNo}:` : `Replace file (from version ${scan.carryOver.fromVersionNo}):`}</b> {scan.carryOver.blanks.carried} blank settings and {scan.carryOver.sections.carried} section rules carried over.</p>
            {scan.carryOver.blanks.needsReassign.length > 0 && (
              <div className={cls.blockBox} style={{ marginTop: ".8rem" }}>
                <b>Needs re-assign ({scan.carryOver.blanks.needsReassign.length}): not found in the new file, not moved</b>
                <ul className={cls.issues}>{scan.carryOver.blanks.needsReassign.map((r) => <li key={r.blankNo}>Old blank {r.blankNo}: {r.words} ({r.setting}){clearBtn("REASSIGN", r)}</li>)}</ul>
              </div>
            )}
            {scan.carryOver.blanks.needsRecheck?.length > 0 && (
              <div className={cls.sugBox} style={{ marginTop: ".8rem" }}>
                <b>Needs re-check ({scan.carryOver.blanks.needsRecheck.length}): changed in Edit in Word (typed over or deleted), setting not carried</b>
                <ul className={cls.issues}>{scan.carryOver.blanks.needsRecheck.map((r) => (
                  <li key={`${r.added ? "a" : "b"}${r.blankNo}`}>Old {r.added ? "added " : ""}blank {r.blankNo} ({r.setting}): {r.now ? <>now “{r.now}”</> : "no longer in the file"} · was …{r.words}…{clearBtn("RECHECK", r)}</li>
                ))}</ul>
              </div>
            )}
          </div>
        )}
      </div>

      <PageSetup versionId={id} version={version} settings={settings} canEdit={canMap}
        onSaved={(r) => setDetail((d) => ({ ...d, version: { ...d.version, pageSetup: r.pageSetup, checkResult: r.checkResult } }))} />

      <div className={cls.panel}>
        <h2 className={cls.panelTitle}>Spaces to fill as a list</h2>
        <div className={cls.filters}>
          <div className={cls.viewTabs} role="tablist" aria-label="View">
            {[["PAGE", "On the page"], ["LIST", "As a list"]].map(([k, t]) => (
              <button key={k} role="tab" aria-selected={view === k} className={`${cls.viewTab} ${view === k ? cls.viewTabOn : ""}`} onClick={() => setView(k)}>{t}</button>
            ))}
          </div>
          {view === "LIST" && (
            <select className={cls.sel} style={{ width: "20rem" }} value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Show">
              {Object.entries(FILTERS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          )}
        </div>
      </div>
      </>}

      {editable && step2 === "WORD" && (
        <EditInWord versionId={id} version={version} canEdit={canEdit}
          onSaved={(r) => {
            successTost(`Version ${r.versionNo} made: ${r.carried} settings carried over${r.needsRecheck ? `, ${r.needsRecheck} to re-check` : ""}${r.needsReassign ? `, ${r.needsReassign} to re-assign` : ""}`);
            navigate(`/home/Templates/${r.docTemplateId}`);
          }}
          onClosed={() => { setStep2("MAP"); load(); }} />
      )}

      {(!editable || step2 === "MAP") && <div className={cls.panel}>
        {inWord && (
          <div className={cls.sugBox} style={{ marginTop: 0, marginBottom: "1.2rem" }}>
            {inWord.mine ? "You have" : `${inWord.byName || "Someone"} has`} this version open in Edit in Word since {fmtDate(inWord.at)}: Map fields is locked until it is saved or closed there.
            {inWord.mine && <> <button className={cls.link} onClick={() => setStep2("WORD")}>Go to Edit in Word</button></>}
          </div>
        )}
        <div className={cls.actionbar}>
          <p className={cls.hint} style={{ margin: 0 }}>
            {view === "PAGE" ? "Click a coloured space on the page to fill it, or a line to change it. Green = filled, yellow = check, red = to fill."
              : "Every space needs one choice: a field from the system, hand-written, or fixed words."}
          </p>
          {canMap && noSetting > 0 && (
            <button className={cls.btn}
              onClick={() => run(() => acceptSuggestions(id), (r) => `${r.accepted} spaces filled from suggestions; ${r.stillWithoutSetting} still to fill`)}>
              Fill all suggested spaces
            </button>
          )}
        </div>
        {view === "PAGE" && (
          <MapFieldsPage versionId={id} blanks={blanks} fields={fields} settings={settings} canEdit={canMap} onSave={saveBlank} onEdit={setEditing} onFixesChanged={putFixes} onBlanksChanged={putAddedBlanks} saving={saving} setSaving={setSaving}
            onAllBlanks={(rows, newBlocks) => setDetail((d) => ({ ...d, blanks: rows, version: { ...d.version, blocks: newBlocks || d.version.blocks } }))}
            onBlocksChanged={(rows, newBlocks) => setDetail((d) => {
              const byNo = Object.fromEntries(rows.map((r) => [r.blankNo, r]));
              return {
                ...d,
                version: { ...d.version, blocks: newBlocks || d.version.blocks },
                blanks: d.blanks.map((b) => (b.blankNo in byNo ? { ...b, blockKey: byNo[b.blankNo].blockKey, applicantPick: byNo[b.blankNo].applicantPick } : b)),
              };
            })}
            shownApplicants={shownApplicants} setShownApplicants={setShownApplicants} focus={focus} />
        )}
        {view === "LIST" && <div className={cls.tblw}>
          <table className={cls.tbl}>
            <thead>
              <tr><th>#</th><th>Found</th><th>Words around the blank</th><th>Suggestion</th><th>Setting</th><th className={cls.last}>{canMap ? "Edit" : ""}</th></tr>
            </thead>
            <tbody>
              {shown.map((b) => (
                <tr key={b.blankNo}>
                  <td className={cls.main}>{b.blankNo}</td>
                  <td>{BLANK_KINDS[b.kind] || b.kind}<br /><span className={cls.mono}>{String(b.rawText || "").slice(0, 28)}</span></td>
                  <td style={{ minWidth: "28rem" }}>…{b.anchorBefore} <b>____</b> {b.anchorAfter}…</td>
                  <td>{b.suggestedFieldKey ? <span>{fieldsByKey[b.suggestedFieldKey]?.label || b.suggestedFieldKey}<br /><span className={cls.hint}>{SUGGESTION_SOURCE[b.suggestionSource]}</span></span>
                    : b.suggestionSource === "PEN" ? <span>Leave blank<br /><span className={cls.hint}>{SUGGESTION_SOURCE.PEN}</span></span> : <span className={cls.muted}>—</span>}</td>
                  <td>{settingText(b, fieldsByKey)}</td>
                  <td className={cls.last}>{canMap && <button className={cls.pillEdit} onClick={() => setEditing(b)}>Edit</button>}</td>
                </tr>
              ))}
              {!shown.length && <tr><td colSpan={6} className={cls.empty}>No blanks in this view</td></tr>}
            </tbody>
          </table>
        </div>}
      </div>}

      {nav(null, [3, "QR"])}
      </>}

      {current === 3 && <>
        <div className={cls.panel}>
          <h2 className={cls.panelTitle}>QR <span className={cls.count}>· the pages of the print</span></h2>
          <QrStep versionId={id} canEdit={canMap} fields={fields}
            lockedWhy={!editable ? `${(STATUS[version.status] || {}).label || version.status}: read only. Upload a new version to change the QR pages.`
              : inWord ? "Open in Edit in Word: the QR pages can be changed after it is saved or closed there." : null} />
        </div>
        {nav([2, "Back"], [4, "Check & preview"])}
      </>}

      {current === 4 && <>
        <div className={cls.panel}>
          <h2 className={cls.panelTitle}>Check &amp; preview <span className={cls.count}>· with a real booking</span></h2>
          <BookingPreview versionId={id} />
        </div>
        {nav([3, "Back"], [5, editable ? "Send for approval" : "Approval"])}
      </>}

      {current === 5 && <>
      {editable && canDo(user, "Send for approval") && (
        <div className={cls.panel}>
          <h2 className={cls.panelTitle}>Send for approval</h2>
          {pending.length > 0 ? (
            <div className={cls.blockBox}>
              <b>What's pending ({pending.length}): these stop Send for approval</b>
              <ul className={cls.pendList}>
                {pending.map((p) => (
                  <li key={p.code + p.message}>
                    <span>{p.message}{p.howTo && <span className={cls.pendHow}>{p.howTo}</span>}</span>
                    {p.show && <button className={cls.btn} onClick={() => showMe(p)}>Show me</button>}
                  </li>
                ))}
              </ul>
            </div>
          ) : <p className={cls.ok} style={{ marginBottom: "1.2rem" }}>Nothing pending ✓</p>}
          {problems.length > 0 && <ul className={cls.issues}>{problems.map((p) => <li key={p} className={cls.bad}>{p}</li>)}</ul>}
          <label className={cls.lbl} htmlFor="sendNote">Note for the approver</label>
          <textarea id="sendNote" className={cls.area} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What changed, what to look at" />
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1.6rem" }}>
            <button className={`${cls.btn} ${cls.primary}`} disabled={pending.length > 0}
              onClick={() => run(() => sendForApproval(id, note), "Sent to the approver")}>Send for approval</button>
          </div>
        </div>
      )}

      {version.status === "IN_APPROVAL" && canDo(user, "Approve") && (
        <div className={cls.panel}>
          <h2 className={cls.panelTitle}>1 · Preview with a real booking</h2>
          {version.sendNote && <p style={{ marginBottom: "1.2rem" }}><b>Note from Legal:</b> {version.sendNote}</p>}
          <p className={cls.hint} style={{ marginBottom: "1.2rem" }}>Check the PDF page by page as the booking would get it, then decide below.</p>
          <BookingPreview versionId={id} onPreviewed={setPreviewed} onTried={() => setTried(true)} />
        </div>
      )}

      {version.status === "IN_APPROVAL" && canDo(user, "Approve") && (
        <div className={cls.panel}>
          <h2 className={cls.panelTitle}>2 · Your decision</h2>
          <p className={cls.hint}>What to check: {blanks.filter((b) => b.setting === "DATA" && !b.removed).length} blanks from data · {blanks.filter((b) => b.setting === "LEAVE_BLANK" && !b.removed).length} left blank · {blanks.filter((b) => b.setting === "FIXED_TEXT" && !b.removed).length} fixed text · {blanks.filter((b) => b.removed).length} removed · {fixes.length} page fixes · checks {blocks.length ? "✕" : "✓"}</p>
          {(() => {
            const needsPreview = settings.approvalNeedsPreview !== false;
            const seen = previewed ? [previewed.project, previewed.wing, previewed.unitNo].filter(Boolean).join(" · ") || previewed.allotmentPaymentId : null;
            return (
              <p className={seen ? cls.ok : cls.hint} style={{ marginTop: ".8rem" }}>
                {seen ? `You previewed it with ${seen}` : needsPreview ? (tried ? "The preview was not made: Approve needs one. You can still return it with comments." : "Make the PDF with a booking above first") : "Preview it with a booking above before you decide"}
              </p>
            );
          })()}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "1.2rem", marginTop: "1.6rem" }}>
            <button className={`${cls.btn} ${cls.danger}`} disabled={settings.approvalNeedsPreview !== false && !previewed && !tried}
              onClick={() => { setComments(""); setReturning(true); }}>Return with comments</button>
            <button className={`${cls.btn} ${cls.primary}`} disabled={settings.approvalNeedsPreview !== false && !previewed}
              onClick={() => run(() => approveTemplate(id, undefined, previewed ? { allotmentPaymentId: previewed.allotmentPaymentId } : undefined), (r) => (r.live ? `Approved. New drafts use version ${version.versionNo}${r.learnedMergeFields?.length ? ` · learned ${r.learnedMergeFields.length} merge field(s)` : ""}` : "Approved; waiting for the next approver"))}>
              Approve
            </button>
          </div>
        </div>
      )}

      {fixes.length > 0 && (version.status !== "DRAFT" || canDo(user, "Approve")) && (
        <div className={cls.panel}>
          <h2 className={cls.panelTitle}>Page fixes <span className={cls.count}>· {fixes.length} · Legal's Word file is not changed; these are applied when a document is made</span></h2>
          <div className={cls.tblw}>
            <table className={cls.tbl}>
              <thead><tr><th>Paragraph</th><th>What</th><th>Old text</th><th>New text</th><th>Who</th><th>When</th></tr></thead>
              <tbody>
                {fixes.map((e) => (
                  <tr key={e.editId}>
                    <td style={{ minWidth: "20rem" }}>{e.where || ""}</td>
                    <td className={`${cls.main} ${cls.nowrap}`}>{fixTitle(e)}{e.recheck ? <><br /><span className={cls.bad}>Needs re-check</span></> : null}</td>
                    <td style={{ minWidth: "24rem" }}>{e.tool === "ADD_BLANK" ? `…${e.context || ""}…` : e.oldText}</td>
                    <td>{e.tool === "EDIT_TEXT" ? e.newText : e.tool === "ADD_BLANK" ? "a new blank" : e.tool === "NOT_A_BLANK" ? "kept as typed" : e.tool === "TAB_STOP" ? `tab stop at ${e.posCm} cm` : e.tool === "NEW_PAGE" ? "new page" : <span className={cls.bad}>removed</span>}</td>
                    <td className={cls.nowrap}>{e.byName || ""}</td>
                    <td className={cls.nowrap}>{fmtDate(e.at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {((version.workflow?.sent || []).length > 0 || (version.approvals || []).length > 0) && (
        <div className={cls.panel}>
          <h2 className={cls.panelTitle}>History</h2>
          <div className={cls.tblw}>
            <table className={cls.tbl} style={{ minWidth: "60rem" }}>
              <thead><tr><th>When</th><th>Who</th><th>What</th><th>Comments</th></tr></thead>
              <tbody>
                {[...(version.workflow?.sent || []).map((s) => ({ at: s.at, who: s.byName, what: "Sent for approval", text: s.note })),
                  ...(version.approvals || []).map((a) => ({ at: a.at, who: a.byName, what: `${a.decision === "APPROVE" ? `Approved (step ${a.step})` : "Returned"}${a.previewedWith ? ` · previewed with ${a.previewedWith.label}` : ""}`, text: a.comments })),
                  ...(version.workflow?.madeLive ? [{ at: version.workflow.madeLive.at, who: version.workflow.madeLive.byName, what: "Made Live", text: "" }] : [])]
                  .sort((x, y) => x.at - y.at)
                  .map((h, i) => <tr key={i}><td>{fmtDate(h.at)}</td><td>{h.who || ""}</td><td className={cls.main}>{h.what}</td><td>{h.text || ""}</td></tr>)}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {nav([4, "Back"], null)}
      </>}

      {editing && <BlankSettingModal blank={editing} fields={fields} settings={settings} onCancel={() => setEditing(null)} onSave={saveBlank}
        inBlock={inApplicantBlock(version.blocks, editing.blockKey)} isApplicantField={isApplicantField} />}

      {returning && (
        <div className={cls.mask} role="dialog" aria-modal="true" aria-label="Return with comments">
          <div className={cls.modal}>
            <div className={cls.modalHead}><h2>Return with comments</h2><p className={cls.hint}>Legal sees your comments and sends a corrected version.</p></div>
            <div className={cls.modalBody}>
              <textarea className={cls.area} value={comments} onChange={(e) => setComments(e.target.value)} placeholder="What must be changed" aria-label="Comments" />
            </div>
            <div className={cls.modalFoot}>
              <button className={cls.btn} onClick={() => setReturning(false)}>Cancel</button>
              <button className={`${cls.btn} ${cls.primary}`} disabled={!comments.trim()}
                onClick={async () => { setReturning(false); await run(() => returnTemplate(id, comments.trim(), previewed ? { allotmentPaymentId: previewed.allotmentPaymentId } : undefined), "Returned to Legal"); }}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TemplateVersion;
